import React from 'react';
import scrollTo from '../utils/scrollTo';
import {connectToStore} from '../components/Provider';
import PaginatedProductList, {PRODUCTS_PER_PAGE} from '../components/PaginatedProductList';
import PaginatedProductCount from '../components/PaginatedProductCount';
import MobileFilterTakeover from '../components/MobileFilterTakeover';
import ProductFilterList from '../components/ProductFilterList';
import parseTaxonomyLocation from '../utils/parseTaxonomyLocation';
import createTaxonomyLinkWithout from '../utils/createTaxonomyLinkWithout';
import createTaxonomyLinkWith from '../utils/createTaxonomyLinkWith';
import createTaxonomyLinkFromQuery from '../utils/createTaxonomyLinkFromQuery';
import createTaxonomyListFromQuery from '../utils/createTaxonomyListFromQuery';
import find from 'lodash/find';
import {navigate} from 'gatsby';
import SortDropdown from '../components/SortDropdown';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';

const SORT_OPTIONS = [
	{
		value: 'featured',
		title: 'Featured',
	},
	{
		value: 'new',
		title: 'Newest',
	},
	{
		value: 'old',
		title: 'Oldest',
	},
	{
		value: 'priceLow',
		title: 'Lowest Price',
	},
	{
		value: 'priceHigh',
		title: 'Highest Price',
	},
];

const CollectionLanding = ({
	pageContext,
	location,
	openMobileFilterTakeover,
}) => {
	const {seo = {}} = pageContext.collection;
	
	const metaTitle = seo.metaTitle || `${pageContext.collection.title} | Teddy Baldassarre`;
	const openGraphTitle = seo.openGraphTitle || `${pageContext.collection.title} | Teddy Baldassarre`;
	const openGraphImage = seo.openGraphImage;
	const twitterTitle = seo.twitterTitle || `${pageContext.collection.title} | Teddy Baldassarre`;
	const twitterImage = seo.twitterImage;
	
	const query = parseTaxonomyLocation(location);

	const {
		sortBy = 'featured',
		offset = 0,
		collection,
		limit = PRODUCTS_PER_PAGE,
		...filterQuery
	} = query;

	let activeFilters = createTaxonomyListFromQuery(filterQuery);
	activeFilters = activeFilters.map(filter => {
		const foundFilter = find(pageContext.allTaxonomies, {
			_type: filter._type,
			slug: filter.slug,
		});

		return foundFilter || filter;
	});

	const clearFilters = () => {
		navigate(`${location.pathname}`);
	};

	const addFilter = filter => {
		navigate(createTaxonomyLinkWith({
			...query,
			offset: 0
		}, filter._type, filter.slug));
	};

	const removeFilter = filter => {
		navigate(createTaxonomyLinkWithout({
			...query,
			offset: 0
		}, filter._type, filter.slug));
	};

	const commitFilterChange = committedQuery => {
		navigate(createTaxonomyLinkFromQuery({
			sortBy,
			offset: 0,
			collection,
			...committedQuery,
		}));
	};

	const onChangeSort = sortBy => {
		navigate(createTaxonomyLinkFromQuery({
			...query,
			sortBy,
			offset: 0,
		}));
	};

	const onChangePageIndex = pageIndex => {
		window.scrollTo(0, 0);
		
		navigate(createTaxonomyLinkFromQuery({
			...query,
			offset: PRODUCTS_PER_PAGE * pageIndex
		}));
	};

	const header = (
		<div className="pt4 pb4 pt6--md pb8--md pl3 pr3 pl4--md pr4--md">
			<div className="row align--bottom">
				<div className="col c6--md">
					<h1 className="tc tl--md domaine--large mb2 mb0--md">
						Shop {pageContext.collection.title}
					</h1>
				</div>
				<div className="col c6--md pb1--md">
					<ul className="list list--horizontal gutter--mobile tc tr--md align--middle mb4 mb0--md">
						<li className="list__item">
							<PaginatedProductCount
								key={location.href}
								query={query}
							/>
						</li>
						<li className="list__item show--md">
							<SortDropdown
								title="Sort By"
								name="sort"
								options={SORT_OPTIONS}
								value={sortBy}
								onChange={value => onChangeSort(value)}
							/>
						</li>
					</ul>
					<div className="hide--md">
						<button
							title="Filters"
							onClick={openMobileFilterTakeover}
							className="full-width button--secondary-gray"
							name="filters">
							{activeFilters.length > 0 ? `Filters (${activeFilters.length})` : 'Filters'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	const mobileFilterTakeover = (
		<div className="hide--md">
			<MobileFilterTakeover
				query={filterQuery}
				filters={pageContext.collection.filters}
				allTaxonomies={pageContext.allTaxonomies}
				commitFilterChange={commitFilterChange}
			/>
		</div>
	);

	const desktopFilters = (
		<div className="col collection__sidebar filter-list__desktop-container show--md">
			<Reveal>
				<div className="reveal__slide reveal__slide--medium reveal__delay--3">
					<ProductFilterList
						query={filterQuery}
						filters={pageContext.collection.filters}
						activeFilters={activeFilters}
						clearFilters={clearFilters}
						addFilter={addFilter}
						removeFilter={removeFilter}
					/>
				</div>
			</Reveal>
		</div>
	);

	const results = (
		<div className="col collection__main">
			<PaginatedProductList
				key={location.href}
				query={query}
				clearFiltersLink={location.pathname}
				offset={offset}
				limit={limit}
				onChangePageIndex={onChangePageIndex}
			/>
		</div>
	);

	const landingBody = (
		<div className="pl3 pr3 pl4--md pr4--md pb6 pr">
			<div className="row pb6--md">
				{desktopFilters}
				{results}
			</div>
		</div>
	);

	const page = (
		<div className="bg--gray-bg">
			<div className="grid-container contained flush">
				{header}
				{mobileFilterTakeover}
				{landingBody}
			</div>
		</div>
	);

	return (
		<React.Fragment>
			<Seo
				metaTitle={metaTitle}
				metaDescription={seo.metaDescription}
				metaKeywords={seo.metaKeywords}
				openGraphTitle={openGraphTitle}
				openGraphDescription={seo.openGraphDescription}
				openGraphImage={openGraphImage}
				twitterTitle={twitterTitle}
				twitterDescription={seo.twitterDescription}
				twitterImage={twitterImage}
				pathname={location.pathname}
			/>
			{page}
		</React.Fragment>
	);
};

const mapStateToProps = () => {};

const mapStoreToProps = ({
	openMobileFilterTakeover,
}) => ({
	openMobileFilterTakeover,
});

export default connectToStore(mapStateToProps, mapStoreToProps)(CollectionLanding);